import { AiOutlineSearch } from "react-icons/ai";
import { RiArrowUpSFill, RiArrowDownSFill } from "react-icons/ri";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
} from "@tanstack/react-table";
import { FileData, SelectedFiles } from "@/types";
import styles from "./css/CityFilesTable.module.css";
import { useNavigate, useLocation } from "react-router-dom";
import TruncateText from "../addons/TruncatedText";
import { useTranslation } from 'react-i18next';
import { langDirection } from "@/utils/languages";
import CheckboxTableIconHeader from "@/icons/ChecboxTableIconHeader";
import CheckboxTableCellIcon from "@/icons/CheckboxTableCellIcon"
import { formatDate } from "@/utils/date";
import { Button } from "../Buttons/Button";
import { DeleteIcon } from "@/icons/DeleteIcon";
import { DownloadFileIcon } from "@/icons/DownloadFileIcon";

interface FilesTableProps {
  className?: string;
  checkedFiles: SelectedFiles[];
  onCheckboxToggle: (fileId: string) => void;
  handleSelectAll: (selectAll: boolean) => void;
  dataTestId: string;
  isSelected: boolean;
  setSelectedShowFiles: (id: string) => void;
  selectedFileId?: string;
  files: FileData[];
  downloadPDF: (pdfUrl: string, filename: string) => void;
}

export const FilesTable: React.FC<FilesTableProps> = ({
  className,
  checkedFiles,
  onCheckboxToggle,
  handleSelectAll,
  dataTestId,
  isSelected,
  files,
  setSelectedShowFiles,
  selectedFileId,
  downloadPDF
}) => {

  const { t, i18n } = useTranslation('CityFilesTable');
  const direction = langDirection(i18n.language)
  const navigate = useNavigate();
  const colors = ["green", "pink", "yellow", "blue"];
  const randomColor = () => colors[Math.floor(Math.random() * colors.length)];
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const filterInputRef = useRef<HTMLInputElement>(null);
  const [inputFilter, setInputFilter] = useState("");
  const [filteredFiles, setFilteredFiles] = useState<FileData[]>(files);

  const toggleFilter = (columnId: string) => {
    setActiveFilter((prev) => (prev === columnId ? null : columnId));
    setInputFilter("");
  };
  const location = useLocation();
  const decodedPathname = decodeURIComponent(location.pathname);

  const handleFilter = (columnId: string) => {
    switch (columnId) {
      case "name":
        setFilteredFiles(
          files.filter((file) => file.name.toLowerCase().includes(inputFilter))
        );
        break;
      case "created_at":
        setFilteredFiles(
          files.filter((file) =>
            file.created_at.toLowerCase().includes(inputFilter)
          )
        );
        break;
    }
  };

  useEffect(() => {
    if (activeFilter && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [activeFilter]);

  const handleUpdateFile = (fileId: string) => {
    // Create a hidden file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.style.display = 'none';

    // Add it to the document
    document.body.appendChild(fileInput);

    // Handle file selection
    fileInput.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      
      if (file) {
        if (file.type === 'text/csv' || file.name.endsWith('.csv')) {

        } else {
          // TODO: Add proper error handling/notification
          console.error('Please select a CSV file');
        }
      }

      // Clean up
      document.body.removeChild(fileInput);
    };

    // Trigger file selection dialog
    fileInput.click();
  };

  const handleDeleteFile = (fileId: string) => {
  }

  const columns = useMemo<ColumnDef<FileData>[]>(
    () => {
      const baseColumns: ColumnDef<FileData>[] = [
 
        {
            accessorKey: "type",
            header: () => <div data-testid="type-column-header" className={styles.headerText}>{t('Type')}</div>,
            cell: ({ row }) => {
                return (
                    <div data-testid={`type-cell-${row.original.id}`}>{t(row.original.type)}</div>
                );
            },
        },
        {
          accessorKey: "name",
          header: () => <div data-testid="name-column-header" className={styles.headerText}>{t('FileName')}</div>,
          cell: ({ row }) => {
            return (
                <span
                  data-testid={`${dataTestId}-${row.original.name}`}
                  onClick={() => {
                    setSelectedShowFiles(row.original.id)
                  }}
                  className={`${styles.fileName}`}
                >
                  <TruncateText text={row.original.name} maxLength={35} />
                </span>
            );
          },
          sortingFn: (rowA, rowB) => {
            return rowA.original.name.localeCompare(rowB.original.name);
          },
        },
        {
          accessorKey: "created_at",
          header: () => <div data-testid="created-at-column-header" className={styles.headerText}>{t('CreatedAt')}</div>,
          cell: ({ row }) => (
            <div data-testid={`created-at-cell-${row.original.id}`}>
              {formatDate(row.original.created_at)}
            </div>
          ),
          sortingFn: (rowA, rowB) => {
            const parseDateTime = (dateTimeStr: string) => {
              const [date, time] = dateTimeStr.split(' ');
              const [day, month, year] = date.split('/').map(Number);
              const [hours, minutes, seconds] = time.split(':').map(Number);
              return new Date(2000 + year, month - 1, day, hours, minutes, seconds);
            };

            const dateA = parseDateTime(rowA.original.created_at);
            const dateB = parseDateTime(rowB.original.created_at);
            return dateA.getTime() - dateB.getTime();
          },
        },
        {
          accessorKey: "actions",
          header: () => <div data-testid="actions-column-header" className={styles.headerText}>{t("Actions")}</div>,
          cell: ({ row }) => (
            <div data-testid={`actions-cell-${row.original.id}`} className="flex flex-row gap-2 2xl:gap-4">
              <Button
                icon="no"
                text={t("updateFile")}
                id={`view-button-${row.original.id}`}
                onClick={() => handleUpdateFile(row.original.id)}
                ready={true}
                size="fit"
                color="blue"
                
              />
              <Button
                icon="left-side"
                text={""}
                id={`view-button-${row.original.id}`}
                onClick={() => {
                  downloadPDF(row.original.url, row.original.name);
                }}
                ready={true}
                iconComponent={DownloadFileIcon}
                size="fit"
                color="blue"
              />
              <Button
                icon="left-side"
                text={""}
                id={`view-button-${row.original.id}`}
                onClick={() => {
                    handleDeleteFile(row.original.id);
                }}
                color="red"
                ready={true}
                iconComponent={DeleteIcon}
                size="fit"
              />
            </div>
            
          )
        }
      ];

      return baseColumns;
    },
    [navigate, randomColor, decodedPathname]
  );

  const [sorting, setSorting] = useState<SortingState>([]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, columnId: string) => {
    if (event.key === "Enter") {
      handleFilter(columnId);
    }
  };

  const table = useReactTable({
    data: filteredFiles,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (filteredFiles.length === 0) {
    return (
      <div className={`${styles.tableContainer} ${className}`}>
        <table className={styles.emptyTable}>
          <thead className={styles.tableHeadersRow}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`${styles.headerText} ${styles.tableHeader} ${styles[direction]} ${
                      header.id === 'checkbox' ? styles.checkboxHeader : ''
                    }`}
                  >
                    <div>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            <tr>
              {columns.map((column, index) => (
                <td key={index} className={styles.emptyCell}></td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={`${styles.tableContainer} ${className} ${styles[direction]}`}>
      <table className={`${styles.table} ${styles[direction]}`}>
        <thead className={styles.tableHeadersRow}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  data-column={header.column.id}
                  className={`${styles.headerText} ${styles.tableHeader} ${styles[direction]} ${
                    header.id === 'checkbox' ? styles.checkboxHeader : ''
                  }`}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className={styles.bodyRow}>
          {table.getRowModel().rows.map((row) => (
            <tr 
              key={row.id} 
              className={`${styles.tableRow} ${selectedFileId === row.original.id ? styles.selectedRow : ''}`}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  data-column={cell.column.id}
                  className={`${styles.rowText} ${styles.tableCell} ${styles[direction]} 
                    ${cell.column.id === "checkbox" ? styles.checkbox : ""}
                    ${cell.column.id === "name" ? styles.fileName : ""}`}
                  onClick={() => {
                    if (cell.column.id !== "checkbox" && cell.column.id !== "actions") {
                      setSelectedShowFiles(row.original.id);
                    }
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FilesTable;
